import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from '@askporter/config-provider';
import { captureException } from '@askporter/exception-logger';
import { useToast } from '@askporter/toast';
import { copyToClipboard } from '@askporter/utils';
import { useBreadcrumbs } from '../../providers/breadcrumbs/BreadcrumbContext';
import { Breadcrumbs } from '../../stories/Breadcrumbs';
import { generateBreadcrumbItems } from './utils';

export const GlobalBreadcrumbs: React.FC = () => {
  const { displayRoute, navigationRoute, injectedBreadcrumbs, loadingUids, concealedSections, copyableRefs } =
    useBreadcrumbs();
  const location = useLocation();
  const basePath = location.pathname.split('/').filter(Boolean).join('/');
  const { showSuccess, showError } = useToast();
  const { t } = useTranslation();

  const copyFunction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const success = copyToClipboard(`${window.location.origin}/${basePath}`, captureException);
    if (success) {
      showSuccess(t('common:clipboard_success'));
    } else {
      showError(t('common:clipboard_error'));
    }
  };

  const copyRefFunction = (ref: string) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(ref)
        .then(() => showSuccess(t('common:reference_clipboard_success')))
        .catch(() => showError(t('common:clipboard_error')));
      return;
    }
    const success = copyToClipboard(ref, captureException);
    if (success) {
      showSuccess(t('common:reference_clipboard_success'));
    } else {
      showError(t('common:clipboard_error'));
    }
  };

  const items = useMemo(() => {
    return generateBreadcrumbItems({
      displayRoute,
      navigationRoute,
      injectedBreadcrumbs,
      loadingUids,
      concealedSections,
      t,
      copyableRefs,
    });
  }, [displayRoute, navigationRoute, injectedBreadcrumbs, loadingUids, concealedSections, t, copyableRefs]);

  // atm we don't want to show breadcrumbs on assets list or task list pages
  return (
    <>
      {items.length >= 2 && (
        <Breadcrumbs
          items={items}
          highlightLastItem
          onCopy={copyFunction}
          onCopyRef={copyRefFunction}
          copyRefLabel={t('common:copy_reference')}
          t={t}
        />
      )}
    </>
  );
};
